"use client";

import { useCallback, useRef, useState } from "react";

/** Treat "within this many px of the bottom" as pinned to the latest. */
const BOTTOM_THRESHOLD = 80;

/**
 * Stick-to-bottom for a streaming chat log (ChatGPT/Claude behaviour):
 *
 * - **Follows** growing content — new messages, streamed tokens, and the
 *   per-frame typewriter reveal — while the user is at the bottom. A
 *   `ResizeObserver` on the growing inner element drives this, so it tracks
 *   height changes React effects can't see (the typewriter animates outside
 *   Redux).
 * - **Releases** the moment the user scrolls up, so reading history isn't
 *   yanked back down. `atBottom` lets the caller show a "jump to latest" button.
 * - `scrollToBottom()` re-engages following — call it on send / thread switch.
 *
 * `scrollRef` / `contentRef` are **callback refs**, so the listener + observer
 * attach the instant the chat surface mounts (it renders only after the first
 * message, once the greeting clears) rather than on the component's first paint
 * when those nodes don't exist yet.
 */
export function useStickToBottom() {
  const scrollEl = useRef<HTMLDivElement | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const stuck = useRef(true);
  const [atBottom, setAtBottom] = useState(true);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = scrollEl.current;
    if (!el) return;
    stuck.current = true;
    setAtBottom(true);
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollEl.current;
    if (!el) return;
    const near =
      el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD;
    stuck.current = near;
    setAtBottom(near);
  }, []);

  // Callback ref: (de)register the scroll listener as the container mounts.
  const scrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollEl.current?.removeEventListener("scroll", handleScroll);
      scrollEl.current = node;
      node?.addEventListener("scroll", handleScroll, { passive: true });
    },
    [handleScroll],
  );

  // Callback ref: observe the growing content and follow the bottom while stuck.
  // ResizeObserver fires once on observe(), so a fresh mount snaps to the latest.
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect();
    if (!node) return;
    observer.current = new ResizeObserver(() => {
      const el = scrollEl.current;
      if (el && stuck.current) el.scrollTop = el.scrollHeight;
    });
    observer.current.observe(node);
  }, []);

  return { scrollRef, contentRef, atBottom, scrollToBottom };
}
